import type { MdField } from "./field";

/**
 * Formatier-Operationen auf einem Markdown-Textfeld.
 *
 * Zwei Regeln gelten überall:
 *   1. Alle Änderungen laufen über `MdField.insert()` – nur so bleibt das
 *      Undo/Redo des jeweiligen Feldes erhalten.
 *   2. Nach der Änderung wird die Auswahl wiederhergestellt (bzw. auf den neu
 *      eingefügten Text gelegt). Dadurch lassen sich Effekte stapeln:
 *      markieren → Fett → Kursiv, ohne erneut zu markieren.
 */

/** Ein Klick auf ein Bedienelement darf dem Textfeld nicht den Fokus stehlen. */
export function keepSelection<T extends HTMLElement>(el: T): T {
  el.addEventListener("mousedown", (e) => e.preventDefault());
  return el;
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Ersetzt den Bereich [start,end) und legt die Auswahl auf [selStart,selEnd). */
function replaceRange(
  ta: MdField,
  start: number,
  end: number,
  text: string,
  selStart: number,
  selEnd: number
): void {
  ta.focus();
  ta.setSelectionRange(start, end);
  ta.insert(text);
  ta.setSelectionRange(selStart, selEnd);
  ta.emitInput();
}

/**
 * Umschließt die Auswahl mit `before`/`after` – oder entfernt die Auszeichnung
 * wieder, wenn sie schon da ist (innerhalb ODER direkt außerhalb der Auswahl).
 * Ohne Auswahl wird `placeholder` eingesetzt und markiert.
 */
export function wrapSelection(
  ta: MdField,
  before: string,
  after: string,
  placeholder: string
): void {
  const start = ta.selectionStart;
  const end = ta.selectionEnd;
  const sel = ta.value.slice(start, end);
  const min = before.length + after.length;

  // `*` (kursiv) darf `**` (fett) nicht aufknacken.
  const collides = before === "*" && (sel.startsWith("**") || sel.endsWith("**"));

  // a) Marker stehen in der Auswahl -> entfernen
  if (!collides && sel.length >= min && sel.startsWith(before) && sel.endsWith(after)) {
    const inner = sel.slice(before.length, sel.length - after.length);
    replaceRange(ta, start, end, inner, start, start + inner.length);
    return;
  }
  // b) Marker stehen direkt um die Auswahl herum -> entfernen
  const outerBefore = ta.value.slice(Math.max(0, start - before.length), start);
  const outerAfter = ta.value.slice(end, end + after.length);
  if (
    sel &&
    !collides &&
    outerBefore === before &&
    outerAfter === after &&
    !(before === "*" && ta.value.slice(Math.max(0, start - 2), start) === "**")
  ) {
    const from = start - before.length;
    replaceRange(ta, from, end + after.length, sel, from, from + sel.length);
    return;
  }
  // c) Kein Auswahl, aber Cursor steckt in einer aktiven Auszeichnung -> abwählen.
  if (!sel && !collides) {
    const span = findEnclosing(ta.value, start, before, after);
    if (span) {
      const inner = ta.value.slice(span.from + before.length, span.to - after.length);
      const caret = start - before.length;
      replaceRange(ta, span.from, span.to, inner, clamp(caret, span.from, span.from + inner.length), clamp(caret, span.from, span.from + inner.length));
      return;
    }
  }
  // d) sonst umschließen
  const text = sel || placeholder;
  replaceRange(
    ta,
    start,
    end,
    before + text + after,
    start + before.length,
    start + before.length + text.length
  );
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Sucht das nächste `before…after`-Paar, das `pos` umschließt (gleiche Zeile). */
function findEnclosing(
  text: string,
  pos: number,
  before: string,
  after: string
): { from: number; to: number } | null {
  const ls = text.lastIndexOf("\n", pos - 1) + 1;
  const le = (() => {
    const i = text.indexOf("\n", pos);
    return i === -1 ? text.length : i;
  })();
  const line = text.slice(ls, le);
  const col = pos - ls;

  let i = 0;
  while (i < line.length) {
    const b = line.indexOf(before, i);
    if (b === -1) return null;
    const skipBold = before === "*" && line.startsWith("**", b);
    if (skipBold) {
      i = b + 2;
      continue;
    }
    const a = line.indexOf(after, b + before.length);
    if (a === -1) return null;
    if (b <= col && col <= a + after.length) {
      return { from: ls + b, to: ls + a + after.length };
    }
    i = a + after.length;
  }
  return null;
}

export interface LinePrefixOptions {
  /**
   * Was am Zeilenanfang als „gleiche Familie" gilt und beim Setzen weichen
   * muss (z. B. jede Überschriftenebene). Standard: exakt dieses Präfix.
   */
  strip?: RegExp;
  /** Leerzeilen innerhalb der Auswahl unangetastet lassen. Standard: true. */
  skipEmpty?: boolean;
}

/**
 * Setzt ein Zeilen-Präfix (`# `, `- `, `> `, `[ ] `) über alle angefassten
 * Zeilen – oder entfernt es wieder, wenn es überall schon steht.
 * Die Auswahl bleibt inhaltlich erhalten.
 */
export function toggleLinePrefix(
  ta: MdField,
  prefix: string,
  opts: LinePrefixOptions = {}
): void {
  const strip = opts.strip ?? new RegExp("^" + escapeRe(prefix));
  const skipEmpty = opts.skipEmpty ?? true;

  const selStart = ta.selectionStart;
  const selEnd = ta.selectionEnd;
  const blockStart = ta.value.lastIndexOf("\n", selStart - 1) + 1;
  // Endet die Auswahl exakt auf einem Zeilenanfang, gehört diese Zeile nicht dazu.
  const endProbe = selEnd > selStart && ta.value[selEnd - 1] === "\n" ? selEnd - 1 : selEnd;
  const nl = ta.value.indexOf("\n", endProbe);
  const blockEnd = nl === -1 ? ta.value.length : nl;

  const lines = ta.value.slice(blockStart, blockEnd).split("\n");
  // Ganz leere Zeile (kein Text zum „Anfassen") zählt als angefasst – sonst
  // ließe sich in leerem Text nie ein Präfix setzen (Klick auf „Aufgabe"
  // im leeren Editor müsste trotzdem `[ ] ` einfügen).
  const touched = lines.filter((l) => !skipEmpty || l.trim() || lines.length === 1);
  const remove = touched.length > 0 && touched.every((l) => l.startsWith(prefix));

  let firstDelta = 0;
  const next = lines
    .map((line, i) => {
      if (skipEmpty && !line.trim() && lines.length > 1) return line;
      const bare = line.replace(strip, "");
      const out = remove ? bare : prefix + bare;
      if (i === 0) firstDelta = out.length - line.length;
      return out;
    })
    .join("\n");

  if (next === lines.join("\n")) return;
  const totalDelta = next.length - (blockEnd - blockStart);
  const from = Math.max(blockStart, selStart + firstDelta);
  const to = selEnd > selStart ? Math.max(from, selEnd + totalDelta) : from;
  replaceRange(ta, blockStart, blockEnd, next, from, to);
}

/** Präfix-Familien für `toggleLinePrefix`. */
export const STRIP = {
  heading: /^#{1,6}[ \t]+/,
  bullet: /^(?:[-*+]|\d+[.)])[ \t]+/,
  ordered: /^\d+[.)][ \t]+/,
  quote: /^>[ \t]?/,
  task: /^(?:(?:[-*+]|\d+[.)])[ \t]+)?\[[ xX]\][ \t]*/,
} as const;

/**
 * Wandelt die betroffenen Zeilen in eine durchnummerierte Liste (1., 2., …)
 * oder entfernt die Nummerierung wieder, wenn schon alle so anfangen. Anders
 * als `toggleLinePrefix` ist das Präfix hier nicht überall gleich, sondern
 * zählt hoch – ein fester Text würde jede Zeile mit derselben Zahl versehen.
 */
export function toggleOrderedList(ta: MdField): void {
  const selStart = ta.selectionStart;
  const selEnd = ta.selectionEnd;
  const blockStart = ta.value.lastIndexOf("\n", selStart - 1) + 1;
  const endProbe = selEnd > selStart && ta.value[selEnd - 1] === "\n" ? selEnd - 1 : selEnd;
  const nl = ta.value.indexOf("\n", endProbe);
  const blockEnd = nl === -1 ? ta.value.length : nl;

  const lines = ta.value.slice(blockStart, blockEnd).split("\n");
  const touched = lines.filter((l) => l.trim() || lines.length === 1);
  const remove = touched.length > 0 && touched.every((l) => !l.trim() || STRIP.ordered.test(l));

  let firstDelta = 0;
  let n = 0;
  const next = lines
    .map((line, i) => {
      if (!line.trim() && lines.length > 1) return line;
      const bare = line.replace(STRIP.ordered, "").replace(STRIP.bullet, "");
      const out = remove ? bare : `${++n}. ${bare}`;
      if (i === 0) firstDelta = out.length - line.length;
      return out;
    })
    .join("\n");

  if (next === lines.join("\n")) return;
  const totalDelta = next.length - (blockEnd - blockStart);
  const from = Math.max(blockStart, selStart + firstDelta);
  const to = selEnd > selStart ? Math.max(from, selEnd + totalDelta) : from;
  replaceRange(ta, blockStart, blockEnd, next, from, to);
}

/** Fügt Text an der Cursorposition ein (Undo-fähig) und markiert ihn nicht. */
export function insertText(ta: MdField, text: string): void {
  ta.focus();
  ta.insert(text);
  ta.emitInput();
}

const LIST_INDENT = /^([ \t]*)(?:[-*+]|\d+[.)])[ \t]+/;

/**
 * Steht der Cursor in einem Listenpunkt, rückt jede weitere Zeile des
 * Schnipsels so ein, dass sie zum Listenpunkt gehört (CommonMark verlangt
 * das, sonst hängt ein `::: aufklappbar …` lose UNTER der Liste statt DARIN –
 * dann fehlt sein Inhalt in der zusammengeklappten Vorschau).
 */
function indentForList(ta: MdField, text: string): string {
  if (!text.includes("\n")) return text;
  const pos = ta.selectionStart;
  const lineStart = ta.value.lastIndexOf("\n", pos - 1) + 1;
  const line = ta.value.slice(lineStart, pos);
  const m = LIST_INDENT.exec(line);
  if (!m) return text;

  const pad = " ".repeat(m[0].length);
  return text
    .split("\n")
    .map((l, i) => (i === 0 || !l ? l : pad + l))
    .join("\n");
}

/**
 * Fügt einen Baustein ein und setzt den Cursor auf die Markierung `¦` im
 * Schnipsel (nicht ans Ende) – z. B. mitten in eine `::: info … :::`-Box.
 * Ohne `¦` verhält es sich wie `insertText`. In Listenpunkten werden weitere
 * Zeilen automatisch passend eingerückt.
 */
export function insertSnippet(ta: MdField, snippet: string): void {
  const indented = indentForList(ta, snippet);
  const marker = indented.indexOf("¦");
  if (marker === -1) return insertText(ta, indented);

  const start = ta.selectionStart;
  const text = indented.slice(0, marker) + indented.slice(marker + 1);
  ta.focus();
  ta.insert(text);
  const caret = start + marker;
  ta.setSelectionRange(caret, caret);
  ta.emitInput();
}

/** Formatierungs-Zustand an der Cursorposition (für aktive Toolbar-Knöpfe). */
export interface FormatState {
  bold: boolean;
  italic: boolean;
  code: boolean;
  heading: number; // 0 = keine
  quote: boolean;
  task: boolean;
  bullet: boolean;
  ordered: boolean;
}

/** Zählt Vorkommen von `marker` links des Cursors innerhalb der Zeile. */
function inlineActive(line: string, col: number, marker: string): boolean {
  let count = 0;
  let i = 0;
  while (i <= col - marker.length) {
    if (line.startsWith(marker, i)) {
      // `*` nicht mit `**` verwechseln
      if (marker === "*" && (line.startsWith("**", i) || line[i - 1] === "*")) {
        i += 1;
        continue;
      }
      count++;
      i += marker.length;
    } else i++;
  }
  return count % 2 === 1;
}

export function formatStateAt(ta: MdField): FormatState {
  const pos = ta.selectionStart;
  const ls = ta.value.lastIndexOf("\n", pos - 1) + 1;
  const le = ta.value.indexOf("\n", pos);
  const line = ta.value.slice(ls, le === -1 ? ta.value.length : le);
  const col = pos - ls;
  const heading = /^(#{1,6})[ \t]/.exec(line);
  return {
    bold: inlineActive(line, col, "**"),
    italic: inlineActive(line, col, "*"),
    code: inlineActive(line, col, "`"),
    heading: heading ? heading[1].length : 0,
    quote: STRIP.quote.test(line),
    task: STRIP.task.test(line),
    bullet: STRIP.bullet.test(line) && !STRIP.ordered.test(line) && !STRIP.task.test(line),
    ordered: STRIP.ordered.test(line) && !STRIP.task.test(line),
  };
}
